<template>
  <div class="live-quiz-page" :class="{'is-lobby-waiting': isLobbyWaiting, 'is-game-playing': isInGame || isConnectingToGame, 'is-round-over': isRoundOver}">
    <div class="live-quiz-header" v-if="!isLiveExperience">
      <button class="back-button" @click="back">‹</button>
      <div v-if="!isLobbyWaiting">
        <div class="eyebrow">{{ $t('LiveQuiz') }}</div>
        <h1>{{ $t('LiveQuizSchedule') }}</h1>
      </div>
      <div class="question-counter" v-if="!isLobbyWaiting && round.question_count">{{ round.question_count }} {{ $t('Questions') }}</div>
    </div>

    <div class="live-quiz-content" v-if="loaded">
      <section class="lobby-ready-stage" v-if="isLobbyWaiting">
        <img class="lobby-ready-title" src="static/images/livequiz/ready/ready.png" alt="">
        <div class="lobby-timer-card">
          <div class="lobby-timer-caption">{{ $t('LiveQuizTimeRemainingToStart') }}</div>
          <div class="lobby-timer-value">
            <strong>{{ lobbyCountdownParts.minutes }}</strong><span>{{ $t('LiveQuizMinute') }}</span>
            <strong>{{ lobbyCountdownParts.seconds }}</strong><span>{{ $t('LiveQuizSecond') }}</span>
          </div>
          <img class="lobby-timer-divider" src="static/images/livequiz/ready/line.png" alt="">
          <div class="lobby-ready-players">
            <div class="lobby-avatar-stack" aria-hidden="true">
              <img v-for="(avatar, index) in lobbyAvatars" :key="index" :src="avatar" alt="">
            </div>
            <strong>{{ $t('LiveQuizParticipants', {count: formatParticipantCount(round.lobby_player_count || 0)}) }}</strong>
          </div>
        </div>
      </section>

      <section class="quiz-game-stage game-connecting-stage" v-else-if="isConnectingToGame">
        <img class="quiz-game-title" src="static/images/livequiz/start/quiz_ulti.png" alt="">
        <div class="game-connecting-message">{{ $t('LiveQuizLoading') }}</div>
      </section>

      <section class="ready-stage" v-else-if="!isInGame">
        <div class="ready-watermark" aria-hidden="true">READY</div>
        <div class="ready-intro">
          <div class="ready-status">{{ stateLabel }}</div>
          <h2>{{ $t('LiveQuizReadyTitle') }}</h2>
        </div>
        <div class="ready-panel" v-if="round.user_state !== 'ended' && round.user_state !== 'cancelled' && round.user_state !== 'missed'">
          <div class="countdown-caption">{{ countdownTitle }}</div>
          <div class="ready-countdown">{{ countdown }}</div>
          <div class="ready-divider"></div>
          <div class="ready-players">
            <div class="ready-avatar-stack" aria-hidden="true">
              <img src="static/images/default-avatar.png" alt="">
              <img src="static/images/wait-avatar.png" alt="">
              <img src="static/images/rob-avatar.png" alt="">
            </div>
            <strong>{{ $t('LiveQuizReadyPlayers', {count: round.reservation_count || 0}) }}</strong>
          </div>
        </div>
        <div class="ready-meta">
          <span>{{ $t('LiveQuizStartsAt') }} {{ formatTime(round.start_at) }}</span>
          <span v-if="round.question_count">{{ round.question_count }} {{ $t('Questions') }}</span>
        </div>
        <button class="primary-button ready-button" v-if="round.user_state === 'can_reserve'" @click="reserveRound">
          {{ $t('LiveQuizReserve') }}
        </button>
        <div class="reserved-badge" v-else-if="round.user_state === 'reserved_countdown'">{{ $t('LiveQuizReserved') }}</div>
        <div class="hint" v-else-if="round.user_state === 'missed'">{{ $t('LiveQuizMissedEntry') }}</div>
        <div class="hint" v-else-if="round.user_state === 'reservation_closed'">{{ $t('LiveQuizReservationClosed') }}</div>
        <div class="hint" v-else-if="round.user_state === 'ended'">{{ $t('LiveQuizEnded') }}</div>
      </section>

      <section class="quiz-game-stage" v-else>
        <img v-if="phase !== 'ended'" class="quiz-game-title" src="static/images/livequiz/start/quiz_ulti.png" alt="">
        <div class="reconnect-box" v-if="connectionLost && phase !== 'ended'">
          <p>{{ $t('LiveQuizReconnectHint') }}</p>
          <button class="reconnect-button" @click="enterRound">{{ $t('LiveQuizReconnect') }}</button>
        </div>
        <template v-if="isRoundOver">
          <section class="round-over-screen">
            <header class="round-over-header">
              <h1><span>{{ $t('LiveQuizUltimateQuiz') }}</span><strong>{{ $t('LiveQuizWinYourPrizes') }}</strong></h1>
              <div class="round-over-actions">
                <button :aria-label="$t('LiveQuizBackToSchedule')" @click="back"><img src="static/images/livequiz/over/close.png" alt=""></button>
                <button :aria-label="$t('LiveQuizHome')" @click="goHome"><img src="static/images/livequiz/over/home.png" alt=""></button>
              </div>
            </header>

            <template v-if="roundOverStage === 'settling'">
              <div class="round-over-settling">
                <h2>{{ $t('LiveQuizRoundOver') }}</h2>
                <img src="static/images/livequiz/over/calculaing.png" :alt="$t('LiveQuizCalculatingPassRewards')">
              </div>
            </template>
            <template v-else>
              <div class="round-over-pass-summary">
                <div class="round-over-pass-row">
                  <img src="static/images/livequiz/over/malt_L.png" alt="">
                  <div class="round-over-avatar-stack" aria-hidden="true"><img v-for="(avatar, index) in lobbyAvatars" :key="index" :src="avatar" alt=""></div>
                  <strong>{{ roundOverPassCount }} {{ $t('LiveQuizPeople') }}</strong>
                  <img src="static/images/livequiz/over/malt_R.png" alt="">
                </div>
                <p>{{ $t('LiveQuizClearedLevel') }}</p>
              </div>
              <div class="round-over-result-summary">
                <h2>{{ $t('LiveQuizRoundOver') }}</h2>
                <p>{{ $t('LiveQuizAveragePassReward') }}</p>
                <div><strong>{{ roundOverAverageReward }}</strong><em>{{ roundOverRewardUnit }}</em></div>
              </div>
            </template>

            <section class="round-over-next-card" v-if="roundOverNextRound && roundOverNextRound.id">
              <div class="round-over-next-heading">
                <img src="static/images/livequiz/malt_r.png" alt="">
                <h3>{{ $t('LiveQuizNextMatch', {time: formatRoundClock(roundOverNextRound.start_at)}) }}</h3>
                <img src="static/images/livequiz/malt_l.png" alt="">
              </div>
              <div class="round-over-next-prize">
                <p>{{ $t('LiveQuizMaxRewardHint', {count: roundOverNextRound.question_count || 0}) }}</p>
                <div><strong>{{ roundOverNextRound.reward_total || roundOverNextRound.max_points || 0 }}</strong><em>{{ roundOverNextRound.reward_type === 'loyalty' ? $t('LiveQuizLoyalty') : $t('LiveQuizPts') }}</em></div>
              </div>
              <button disabled>{{ $t('LiveQuizStartingSoon') }}</button>
            </section>
          </section>
        </template>
        <template v-else-if="currentQuestion && phase !== 'ended'">
          <div class="game-question-card">
            <div class="question-state-slot">
              <div class="question-timer" v-if="phase === 'answer' || phase === 'reveal'" :style="questionTimerStyle">
                <strong>{{ phase === 'answer' ? remainingSeconds : 0 }}</strong>
              </div>
              <transition name="reveal-title-pop" @after-leave="handleRevealTitleHidden">
                <img v-if="showRevealIntro" class="question-result-title answer-reveal-title"
                     src="static/images/livequiz/start/ans_reveal_title.png" alt="">
              </transition>
              <img v-if="showSubmittedResultIcon && !showRevealIntro && !revealTitleLeaving"
                   class="question-result-icon" :src="questionResultIcon" alt="">
            </div>
            <transition name="personal-result-pop">
              <img v-if="showPersonalResultTitle" class="personal-result-animation"
                   :src="personalResultTitleImage" alt="">
            </transition>
            <div class="question-number">{{ $t('Question') }} {{ currentQuestionNo }}</div>
            <img v-if="currentQuestion.image" class="question-image" :src="currentQuestion.image" alt="">
            <h2 class="question-title">{{ questionTitle }}</h2>
            <div class="answer-list">
              <button v-for="(option, key) in questionOptions" :key="key" class="answer-option"
                      :class="answerClass(key)" :style="answerFillStyle(key)"
                      :aria-disabled="!canAnswer" @click="submitAnswer(key)">
                <span class="answer-text">{{ option }}</span>
                <span class="answer-result-count" v-if="phase === 'reveal'">
                  <em v-if="submittedAnswer === key">{{ $t('LiveQuizSelected') }} | </em>{{ formatParticipantCount(optionCounts[key] || 0) }}
                </span>
                <img v-else-if="submittedAnswer === key" class="answer-selected-check"
                     src="static/images/livequiz/start/check.png" alt="">
              </button>
            </div>
            <p class="question-hint" :class="questionHintClass">{{ questionHint }}</p>
          </div>
        </template>
        <div v-else class="lobby-state">
          <h2>{{ $t('LiveQuizEnded') }}</h2>
          <p>{{ endedMessage }}</p>
        </div>
      </section>
    </div>

    <div class="live-experience-chrome" v-if="loaded && isLiveExperience && !isRoundOver">
      <button class="live-experience-back" :aria-label="$t('Back')" @click="back">
        <img src="static/images/livequiz/back.png" alt="">
      </button>
      <div class="lobby-prize-pool live-experience-prize">
        <p><span>{{ $t('LiveQuizPrizePool') }}</span><strong>{{ roundRewardTotal }}</strong><em>{{ roundRewardUnit }}</em></p>
        <div>{{ $t('LiveQuizCompleteAllToShare', {count: currentQuestionCount || round.question_count || 0}) }}</div>
      </div>
    </div>

    <div class="reward-mask" v-if="showFailureReward">
      <div class="failure-gift-dialog" role="dialog" aria-modal="true" :aria-label="$t('LiveQuizParticipationGift')">
        <p class="failure-gift-participated">
          {{ $t('LiveQuizParticipatedQuestions', {count: failureQuestionNo || currentQuestionNo}) }}
        </p>
        <div class="failure-gift-title">
          <span>{{ $t('LiveQuizGiftSurprise') }}</span>
          <strong>{{ $t('LiveQuizGiftBox') }}</strong>
        </div>
        <div class="failure-gift-visual">
          <img class="failure-gift-box" src="static/images/livequiz/faile/gift_box.png" alt="">
          <div class="failure-gift-bubble">
            <img src="static/images/livequiz/faile/txt_box.png" alt="">
            <strong>{{ $t('LiveQuizGiftUpToPoints', {points: 100}) }}</strong>
          </div>
        </div>
        <button class="failure-gift-button failure-gift-claim" :disabled="claimingFailureReward" @click="claimParticipationGift">
          <img src="static/images/livequiz/faile/claim_btn_bg.png" alt="">
          <span>{{ claimingFailureReward ? $t('LiveQuizGiftClaiming') : $t('LiveQuizContinueSpectating') }}</span>
        </button>
        <button class="failure-gift-button failure-gift-remind" @click="leaveFailureReward">
          <img src="static/images/livequiz/faile/remind_btn_bg.png" alt="">
          <span>{{ $t('LiveQuizRemindTomorrow') }}</span>
        </button>
        <button class="failure-gift-close" :aria-label="$t('LiveQuizCloseGift')" @click="leaveFailureReward">
          <img src="static/images/livequiz/faile/close.png" alt="">
        </button>
      </div>
    </div>
    <div class="final-countdown-mask" v-if="showLobbyFinalCountdown" aria-live="assertive">
      <img class="final-countdown-ready" src="static/images/livequiz/ready/ready.png" alt="">
      <div class="final-countdown-content">
        <p>{{ $t('LiveQuizPleasePrepare') }}</p>
        <div class="final-countdown-number-slot">
          <img class="final-countdown-number" :src="lobbyFinalCountdownImage" :alt="lobbyRemainingSeconds">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data () {
    return {
      loaded: false,
      round: {},
      ws: null,
      now: Date.now(),
      countdownSeconds: 0,
      countdownStartedAt: Date.now(),
      lobbyFinalCountdownActive: false,
      clockTimer: null,
      refreshTimer: null,
      connectionLost: false,
      phase: '',
      phaseRemainingSeconds: 0,
      phaseCountdownStartedAt: Date.now(),
      playerStatus: '',
      currentQuestion: null,
      currentQuestionNo: 0,
      currentQuestionCount: 0,
      submittedAnswer: '',
      correctAnswer: '',
      optionCounts: {},
      revealTitleLeaving: false,
      pendingFailureReward: false,
      showFailureReward: false,
      claimingFailureReward: false,
      failureQuestionNo: 0,
      failureClaimTimer: null,
      failureRewardTimer: null,
      roundEndedWhileGift: false,
      roundOverStage: '',
      roundOverResult: null,
      roundOverPassedCount: 0,
      roundOverLoading: false,
      endedMessage: ''
    }
  },
  computed: {
    isLiveExperience () {
      return this.isLobbyWaiting || this.isInGame || this.isConnectingToGame
    },
    isLobbyWaiting () {
      return this.round.user_state === 'lobby' && !this.isInGame
    },
    isInGame () {
      return ['answer', 'reveal', 'ended'].indexOf(this.phase) !== -1
    },
    isRoundOver () {
      return this.phase === 'ended' && ['settling', 'result'].indexOf(this.roundOverStage) !== -1
    },
    isConnectingToGame () {
      return !this.isInGame && ['live', 'spectating', 'passed'].indexOf(this.round.user_state) !== -1
    },
    questionTitle () {
      if (!this.currentQuestion) return ''
      return this.currentQuestion.title
    },
    questionOptions () {
      if (!this.currentQuestion) return {}
      return this.currentQuestion.options
    },
    canAnswer () {
      return this.playerStatus === 'active' && this.phase === 'answer' && !this.submittedAnswer && this.remainingSeconds > 0
    },
    remainingSeconds () {
      return this.getRemainingSeconds(this.phaseRemainingSeconds, this.phaseCountdownStartedAt)
    },
    lobbyRemainingSeconds () {
      return this.getRemainingSeconds(this.countdownSeconds, this.countdownStartedAt)
    },
    lobbyCountdownParts () {
      const seconds = this.lobbyRemainingSeconds
      return {
        minutes: String(Math.floor(seconds / 60)).padStart(2, '0'),
        seconds: String(seconds % 60).padStart(2, '0')
      }
    },
    lobbyAvatars () {
      const avatars = Array.isArray(this.round.lobby_avatars) ? this.round.lobby_avatars.filter(Boolean).slice(0, 4) : []
      return avatars.length ? avatars : ['static/images/default-avatar.png', 'static/images/wait-avatar.png', 'static/images/rob-avatar.png']
    },
    roundRewardTotal () {
      return this.round.reward_total === undefined || this.round.reward_total === null ? (this.round.max_points || 0) : this.round.reward_total
    },
    roundRewardUnit () {
      return this.round.reward_type === 'loyalty' ? this.$t('LiveQuizLoyalty') : this.$t('LiveQuizPts')
    },
    showLobbyFinalCountdown () {
      return this.lobbyFinalCountdownActive
    },
    lobbyFinalCountdownImage () {
      return 'static/images/livequiz/ready/' + this.lobbyRemainingSeconds + '.png'
    },
    questionTimerStyle () {
      if (this.phase !== 'answer') return {'--timer-progress': '360deg'}
      const total = Math.max(1, Number(this.round.answer_seconds || this.phaseRemainingSeconds || 1))
      const elapsed = Math.max(0, (this.now - this.phaseCountdownStartedAt) / 1000)
      const remaining = Math.max(0, Number(this.phaseRemainingSeconds || 0) - elapsed)
      const progress = Math.max(0, Math.min(1, (total - remaining) / total))
      return {'--timer-progress': (progress * 360) + 'deg'}
    },
    revealElapsedSeconds () {
      if (this.phase !== 'reveal') return 0
      return Math.max(0, Number(this.phaseRemainingSeconds || 0) - this.remainingSeconds)
    },
    showRevealIntro () {
      return this.phase === 'reveal' && this.revealElapsedSeconds < 1
    },
    isSubmittedCorrect () {
      return !!this.submittedAnswer && !!this.correctAnswer && this.submittedAnswer === this.correctAnswer
    },
    isSubmittedWrong () {
      return !!this.submittedAnswer && !!this.correctAnswer && this.submittedAnswer !== this.correctAnswer
    },
    showPersonalResultTitle () {
      return this.phase === 'reveal' && this.revealElapsedSeconds >= 2 && (this.isSubmittedCorrect || this.isSubmittedWrong)
    },
    showSubmittedResultIcon () {
      return this.phase === 'reveal' && (this.isSubmittedCorrect || this.isSubmittedWrong)
    },
    personalResultTitleImage () {
      return 'static/images/livequiz/start/' + (this.isSubmittedCorrect ? 'correct_title.png' : 'wrong_title.png')
    },
    questionResultIcon () {
      return 'static/images/livequiz/start/' + (this.isSubmittedWrong ? 'wrong.png' : 'correct.png')
    },
    roundOverPassCount () {
      if (this.roundOverResult && this.roundOverResult.pass_count !== undefined) return Number(this.roundOverResult.pass_count || 0)
      return Number(this.roundOverPassedCount || 0)
    },
    roundOverAverageReward () {
      if (!this.roundOverResult) return 0
      return Number(this.roundOverResult.average_pass_reward || this.roundOverResult.average_pass_points || 0)
    },
    roundOverRewardUnit () {
      return this.roundOverResult && this.roundOverResult.reward_type === 'loyalty' ? this.$t('LiveQuizLoyalty') : this.$t('LiveQuizPts')
    },
    roundOverNextRound () {
      return this.roundOverResult && this.roundOverResult.next_round ? this.roundOverResult.next_round : null
    },
    questionHint () {
      if (this.phase === 'answer') {
        if (this.playerStatus !== 'active') return this.$t('LiveQuizSpectatorHint')
        return this.submittedAnswer ? this.$t('LiveQuizSelectedHint') : this.$t('LiveQuizChooseAnswer')
      }
      if (this.isSubmittedCorrect) {
        return this.$t('LiveQuizCorrectNextQuestion', {seconds: this.remainingSeconds})
      }
      if (this.isSubmittedWrong) return this.$t('LiveQuizWrongContinueSpectating')
      if (this.playerStatus !== 'active') return this.$t('LiveQuizSpectatorHint')
      return this.$t('LiveQuizAnswerReveal')
    },
    questionHintClass () {
      return {
        correct: this.phase === 'reveal' && this.isSubmittedCorrect,
        wrong: this.phase === 'reveal' && this.isSubmittedWrong,
        spectator: this.playerStatus !== 'active'
      }
    },
    countdown () {
      const seconds = this.getRemainingSeconds(this.countdownSeconds, this.countdownStartedAt)
      const hours = String(Math.floor(seconds / 3600)).padStart(2, '0')
      const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')
      const secs = String(seconds % 60).padStart(2, '0')
      return hours + ':' + minutes + ':' + secs
    },
    countdownTitle () {
      return this.round.user_state === 'can_reserve' || this.round.user_state === 'reserved_countdown'
        ? this.$t('LiveQuizReserveEndsIn') : this.$t('LiveQuizStartsIn')
    },
    stateLabel () {
      const map = {
        not_open: 'LiveQuizNotOpen', can_reserve: 'LiveQuizReservationOpen', reserved_countdown: 'LiveQuizReserved',
        lobby: 'LiveQuizLobby', live: 'LiveQuizPlaying', spectating: 'LiveQuizSpectating', passed: 'LiveQuizPassed',
        missed: 'LiveQuizStatusStarted', reservation_closed: 'LiveQuizReservationClosed', ended: 'LiveQuizEnded', cancelled: 'LiveQuizCancelled'
      }
      return this.$t(map[this.round.user_state] || 'LiveQuiz')
    }
  },
  watch: {
    lobbyRemainingSeconds (seconds) {
      if (!this.isInGame && this.countdownSeconds > 0 && seconds <= 3) {
        this.lobbyFinalCountdownActive = true
      }
    }
  },
  created () {
    this.loadRound()
    this.clockTimer = setInterval(() => { this.now = Date.now() }, 250)
    this.refreshTimer = setInterval(() => {
      if (!this.isInGame) this.loadRound(false)
    }, 15000)
  },
  beforeDestroy () {
    clearInterval(this.clockTimer)
    clearInterval(this.refreshTimer)
    clearTimeout(this.failureClaimTimer)
    clearTimeout(this.failureRewardTimer)
    if (this.ws) this.ws.close()
  },
  methods: {
    loadRound (showLoading = true) {
      const roundId = this.$route.query.round_id
      if (!roundId) {
        this.$router.replace({path: '/'})
        return
      }
      if (showLoading) this.$parent.loadModal(true)
      this.$api.get('/api/live_quiz/detail', {round_id: roundId}, r => {
        if (showLoading) this.$parent.loadModal(false)
        this.$parent.returnCode(r, () => {
          const round = r.data || {}
          if (round.status === 'ended') {
            this.goResult(round.id)
            return
          }
          if (['missed', 'reservation_closed'].indexOf(round.user_state) !== -1) {
            this.loaded = false
            this.$utils.alertText(this.$t('LiveQuizUnableToJoin'), 80)
            this.$router.replace({path: '/liveQuizList'})
            return
          }
          this.applyRound(round)
          this.loaded = true
          if (this.shouldAutoEnter(round)) this.enterRound()
        })
      })
    },
    reserveRound () {
      this.$api.post('/api/live_quiz/reserve', {round_id: this.round.id}, r => {
        this.$parent.returnCode(r, () => {
          this.applyRound(r.data || this.round)
          this.$utils.alertText(this.$t('LiveQuizReserved'), 80)
        })
      })
    },
    enterRound () {
      // 同一场次只保留一个连接；登录成功后会自动发送 enter_lobby。
      if (this.ws) return
      this.ws = new WebSocket('wss://battle.goquizgo.com/ws')
      this.ws.onopen = () => {
        this.ws.send(JSON.stringify({
          match_type: 'live_quiz', action: 'login', token: localStorage.getItem('token'), lang: localStorage.getItem('lang') || 'bur'
        }))
      }
      this.ws.onmessage = event => this.handleMessage(JSON.parse(event.data))
      this.ws.onerror = () => this.$utils.alertText(this.$t('LiveQuizConnectionError'), 80)
      this.ws.onclose = () => {
        this.ws = null
        this.connectionLost = this.phase !== 'ended'
      }
    },
    sendEnterLobby () {
      this.ws.send(JSON.stringify({
        match_type: 'live_quiz',
        action: 'enter_lobby',
        round_id: this.round.id,
        lang: localStorage.getItem('lang') || 'bur'
      }))
    },
    submitAnswer (answer) {
      if (!this.canAnswer) {
        if (this.playerStatus !== 'active') this.$utils.alertText(this.$t('LiveQuizSpectatorHint'), 80)
        return
      }
      this.submittedAnswer = answer
      this.ws.send(JSON.stringify({
        match_type: 'live_quiz', action: 'submit_answer', round_id: this.round.id, question_no: this.currentQuestionNo, answer: answer
      }))
    },
    handleMessage (data) {
      if (data.match_type !== 'live_quiz') return
      if (data.round_id && Number(data.round_id) !== Number(this.round.id)) return
      switch (data.action) {
        case 'login_success':
          this.connectionLost = false
          this.sendEnterLobby()
          break
        case 'lobby_state':
          // 已经进入等待区时保留候场页面，不再显示独立的观战候场界面。
          this.phase = ''
          this.playerStatus = 'active'
          this.syncCountdown(data.countdown_seconds)
          this.round.lobby_player_count = Number(data.lobby_player_count || 0)
          this.round.lobby_avatars = Array.isArray(data.lobby_avatars) ? data.lobby_avatars : []
          break
        case 'round_state':
          this.phase = data.phase
          this.syncPhaseCountdown(data.remaining_seconds)
          this.playerStatus = data.player_status || 'spectating'
          break
        case 'new_question':
          this.lobbyFinalCountdownActive = false
          this.revealTitleLeaving = false
          clearTimeout(this.failureRewardTimer)
          if (this.pendingFailureReward) {
            this.showFailureReward = true
            this.pendingFailureReward = false
          }
          this.currentQuestion = data.question
          this.currentQuestionNo = data.question_no
          this.currentQuestionCount = data.question_count
          this.phase = data.phase
          this.syncPhaseCountdown(data.remaining_seconds)
          this.submittedAnswer = data.submitted_answer || ''
          this.correctAnswer = ''
          this.optionCounts = {}
          break
        case 'answer_locked': this.submittedAnswer = data.answer; break
        case 'question_result':
          this.revealTitleLeaving = true
          this.phase = 'reveal'
          this.syncPhaseCountdown(data.remaining_seconds)
          this.correctAnswer = data.correct_answer
          this.optionCounts = data.option_counts || {}
          if (this.pendingFailureReward) {
            const revealDelay = Math.min(1800, Math.max(500, Number(data.remaining_seconds || 1) * 1000 - 200))
            clearTimeout(this.failureRewardTimer)
            this.failureRewardTimer = setTimeout(() => {
              if (!this.pendingFailureReward || this.playerStatus !== 'eliminated') return
              this.showFailureReward = true
              this.pendingFailureReward = false
            }, revealDelay)
          }
          break
        case 'eliminated':
          this.playerStatus = 'eliminated'
          this.failureQuestionNo = Number(data.question_no || this.currentQuestionNo || 0)
          this.pendingFailureReward = true
          break
        case 'settlement_pending':
          this.beginRoundOver(data)
          break
        case 'round_ended':
          if (this.showFailureReward || this.pendingFailureReward) {
            clearTimeout(this.failureRewardTimer)
            this.showFailureReward = true
            this.pendingFailureReward = false
            this.roundEndedWhileGift = true
            break
          }
          this.loadRoundOverResult(data)
          break
        case 'error':
          this.$utils.alertText(data.message, 80)
          if (['round_already_started', 'round_unavailable'].indexOf(data.error_code) !== -1) {
            if (this.ws) this.ws.close()
            this.$router.replace({path: '/liveQuizList'})
          }
          break
      }
    },
    answerClass (key) {
      return {
        selected: this.submittedAnswer === key,
        correct: this.phase === 'reveal' && this.correctAnswer === key,
        wrong: this.phase === 'reveal' && this.submittedAnswer === key && this.correctAnswer !== key
      }
    },
    answerFillStyle (key) {
      if (this.phase !== 'reveal') return {}
      const total = Object.keys(this.optionCounts || {}).reduce((sum, optionKey) => sum + Number(this.optionCounts[optionKey] || 0), 0)
      if (!total || (key !== this.correctAnswer && key !== this.submittedAnswer)) return {}
      const percent = Math.max(18, Math.min(100, Math.round(Number(this.optionCounts[key] || 0) / total * 100)))
      return {'--answer-fill-width': percent + '%'}
    },
    handleRevealTitleHidden () { this.revealTitleLeaving = false },
    claimParticipationGift () {
      if (this.claimingFailureReward || !this.round.id) return
      this.claimingFailureReward = true
      clearTimeout(this.failureClaimTimer)
      this.failureClaimTimer = setTimeout(() => { this.claimingFailureReward = false }, 12000)
      this.$api.post('/api/live_quiz/claimparticipationgift', {round_id: this.round.id}, r => {
        clearTimeout(this.failureClaimTimer)
        this.claimingFailureReward = false
        clearTimeout(this.failureRewardTimer)
        this.showFailureReward = false
        this.pendingFailureReward = false
        this.$nextTick(() => {
          this.$parent.returnCode(r, () => {
            this.playerStatus = 'spectating'
            this.$utils.alertText(this.$t('LiveQuizPointsReceived', {points: Number(r.data.points || 0)}), 80)
            if (this.roundEndedWhileGift) this.loadRoundOverResult()
          })
        })
      })
    },
    leaveFailureReward () {
      clearTimeout(this.failureRewardTimer)
      this.showFailureReward = false
      this.pendingFailureReward = false
      if (this.ws) this.ws.close()
      this.$router.replace({path: '/liveQuizList'})
    },
    getRemainingSeconds (seconds, startedAt) {
      const elapsed = Math.floor((this.now - startedAt) / 1000)
      return Math.max(0, Number(seconds || 0) - elapsed)
    },
    syncCountdown (seconds) {
      this.countdownSeconds = Number(seconds || 0)
      if (this.countdownSeconds > 3) this.lobbyFinalCountdownActive = false
      this.now = Date.now()
      this.countdownStartedAt = this.now
    },
    syncPhaseCountdown (seconds) {
      this.phaseRemainingSeconds = Number(seconds || 0)
      this.now = Date.now()
      this.phaseCountdownStartedAt = this.now
    },
    applyRound (round) {
      this.round = round
      this.syncCountdown(round.countdown_seconds)
    },
    shouldAutoEnter (round) {
      return ['lobby', 'live', 'spectating', 'passed'].indexOf(round.user_state) !== -1
    },
    beginRoundOver (data = {}) {
      this.lobbyFinalCountdownActive = false
      this.phase = 'ended'
      this.connectionLost = false
      this.roundOverStage = 'settling'
      this.roundOverPassedCount = Number(data.pass_count || this.roundOverPassedCount || 0)
    },
    loadRoundOverResult (data = {}) {
      this.beginRoundOver(data)
      if (this.roundOverLoading || !this.round.id) return
      this.roundOverLoading = true
      this.$api.get('/api/live_quiz/roundresult', {round_id: this.round.id}, r => {
        this.roundOverLoading = false
        this.$parent.returnCode(r, () => {
          this.roundOverResult = r.data || {}
          this.roundOverPassedCount = Number(this.roundOverResult.pass_count || this.roundOverPassedCount || 0)
          this.roundOverStage = 'result'
        })
      })
    },
    goResult (roundId) {
      if (!roundId) return
      if (this.ws) this.ws.close()
      this.$router.replace({path: '/liveQuizResult', query: {round_id: roundId}})
    },
    formatTime (value) {
      if (!value) return ''
      const parts = String(value).split(' ')
      return (parts[0] || '') + ' ' + (parts[1] || '').slice(0, 5)
    },
    formatRoundClock (value) {
      if (!value) return ''
      return (String(value).split(' ')[1] || '').slice(0, 5)
    },
    formatParticipantCount (count) {
      count = Math.max(0, Number(count) || 0)
      if (count >= 1000000) return (count / 1000000).toFixed(count >= 10000000 ? 0 : 1).replace(/\.0$/, '') + 'M'
      if (count >= 1000) return (count / 1000).toFixed(count >= 10000 ? 0 : 1).replace(/\.0$/, '') + 'K'
      return String(Math.floor(count))
    },
    back () { this.$router.push({path: '/liveQuizList'}) },
    goHome () { this.$router.push({path: '/'}) }
  }
}
</script>

<style scoped>
.live-quiz-page { position:relative; min-height:100vh; overflow:hidden; padding:30px 28px 88px; box-sizing:border-box; color:#fff; background:radial-gradient(circle at 50% 79%,rgba(22,105,240,.32),transparent 25%),radial-gradient(circle at 100% 10%,rgba(116,59,238,.32),transparent 33%),#10112d; }
.live-quiz-page:before,.live-quiz-page:after { content:''; position:absolute; width:240px; height:240px; border-radius:50%; filter:blur(8px); opacity:.5; pointer-events:none; }.live-quiz-page:before { top:118px; left:-155px; background:radial-gradient(circle,#ec4bdd 0 2px,transparent 3px); background-size:18px 18px; }.live-quiz-page:after { top:90px; right:-155px; background:radial-gradient(circle,#8457ff 0 2px,transparent 3px); background-size:18px 18px; }
.live-quiz-header { position:relative; z-index:1; display:flex; align-items:center; gap:16px; max-width:670px; margin:0 auto 28px; }.live-quiz-header h1 { max-width:480px; margin:3px 0 0; font-size:31px; line-height:1.1; }.eyebrow { color:#f8d875; font-size:18px; font-weight:800; letter-spacing:2px; text-transform:uppercase; }.back-button { width:48px; height:48px; flex:none; border:1px solid rgba(255,255,255,.78); border-radius:50%; color:#fff; background:rgba(255,255,255,.05); font-size:44px; line-height:35px; }.question-counter { margin-left:auto; padding:9px 13px; border:1px solid rgba(255,255,255,.14); border-radius:15px; color:#e8e3ff; background:rgba(255,255,255,.12); font-size:17px; line-height:1.25; text-align:center; }
.live-quiz-page.is-lobby-waiting { display:flex; min-height:100vh; flex-direction:column; padding:22px 24px 20px; background:#090126 url('../../static/images/livequiz/ready/bg.jpg') center top/cover no-repeat; }.is-lobby-waiting:before,.is-lobby-waiting:after { display:none; }.is-lobby-waiting .live-quiz-header { width:100%; min-height:48px; justify-content:space-between; margin:0 auto; }.is-lobby-waiting .back-button { width:48px; height:48px; border:0; background:transparent url('../../static/images/livequiz/back.png') center/contain no-repeat; color:transparent; font-size:0; }.lobby-info-button { display:flex; width:48px; height:48px; align-items:center; justify-content:center; }.lobby-info-button img { width:100%; height:100%; object-fit:contain; }.is-lobby-waiting .live-quiz-content { display:flex; width:100%; max-width:670px; flex:1; flex-direction:column; margin:0 auto; }.lobby-ready-stage { position:relative; z-index:1; display:flex; width:100%; min-height:calc(100vh - 90px); flex:1; flex-direction:column; align-items:center; box-sizing:border-box; padding-top:30px; }.lobby-ready-title { display:block; width:86%; max-width:590px; height:auto; margin:0 auto 48px; object-fit:contain; }.lobby-timer-card { position:relative; display:flex; width:100%; max-width:600px; aspect-ratio:900 / 729; align-items:center; box-sizing:border-box; flex-direction:column; padding:13% 10% 8%; background:transparent url('../../static/images/livequiz/ready/timer_bg.png') center/100% 100% no-repeat; }.lobby-timer-caption { color:rgba(224,210,255,.78); font-size:clamp(15px,4vw,25px); font-weight:800; letter-spacing:.4px; text-align:center; text-transform:uppercase; }.lobby-timer-value { display:flex; align-items:flex-end; justify-content:center; gap:6px; margin-top:24px; color:#fff; font-family:Arial,sans-serif; line-height:.82; white-space:nowrap; }.lobby-timer-value strong { font-size:clamp(55px,18vw,120px); font-weight:900; letter-spacing:-4px; }.lobby-timer-value span { padding-bottom:8px; color:#fff; font-size:clamp(19px,5.7vw,36px); font-weight:700; }.lobby-timer-divider { display:block; width:82%; height:auto; margin:28px auto 20px; opacity:.78; }.lobby-ready-players { display:flex; align-items:center; justify-content:center; gap:10px; color:#fbeaff; font-size:clamp(16px,4.8vw,30px); line-height:1.2; text-align:left; }.lobby-ready-players strong { font-weight:800; }.lobby-avatar-stack { display:flex; padding-left:17px; }.lobby-avatar-stack img { width:45px; height:45px; margin-left:-17px; border:2px solid #e9e9ff; border-radius:50%; object-fit:cover; background:#fff; }.lobby-prize-pool { width:calc(100% + 10px); max-width:690px; margin-top:auto; margin-bottom:-8px; padding:19px 23px 13px; box-sizing:border-box; background:transparent url('../../static/images/livequiz/ready/bottom_part_bg.png') center/100% 100% no-repeat; color:#fff; text-align:center; }.lobby-prize-pool p { margin:0 0 10px; font-size:clamp(15px,4.25vw,28px); font-weight:700; line-height:1.25; }.lobby-prize-pool p strong { color:#fff070; font-size:1.42em; font-weight:900; }.lobby-prize-pool p em { color:#fff070; font-size:1.08em; font-style:normal; font-weight:900; }.lobby-prize-pool > div { min-height:33px; padding:8px 16px; box-sizing:border-box; border-radius:11px; background:transparent url('../../static/images/livequiz/ready/bottom_part_txt_bg.png') center/100% 100% no-repeat; color:rgba(239,225,255,.87); font-size:clamp(12px,3.5vw,22px); line-height:1.2; }
.ready-stage { position:relative; z-index:1; max-width:670px; min-height:700px; margin:0 auto; padding-top:54px; text-align:center; }.ready-watermark { position:absolute; top:14px; left:50%; color:rgba(205,198,255,.12); font-size:136px; font-weight:900; font-style:italic; letter-spacing:-10px; line-height:1; transform:translateX(-50%) scaleX(1.06); white-space:nowrap; }.ready-intro { position:relative; z-index:1; margin-bottom:58px; }.ready-status { margin-bottom:10px; color:#f9edcf; font-size:21px; font-weight:800; letter-spacing:.6px; }.ready-intro h2 { margin:0; color:#fff1d8; font-size:35px; line-height:1.2; font-weight:900; }.ready-panel { position:relative; overflow:hidden; padding:52px 36px 40px; border:1px solid rgba(180,181,255,.33); border-radius:38px; background:linear-gradient(180deg,rgba(28,29,83,.92),rgba(18,24,67,.92)); box-shadow:inset 0 1px 0 rgba(255,255,255,.05),0 22px 42px rgba(4,5,31,.35); }.ready-panel:after { content:''; position:absolute; z-index:-1; right:18%; bottom:-75px; width:64%; height:140px; border-radius:50%; background:rgba(21,132,255,.38); filter:blur(30px); }.countdown-caption { color:rgba(214,216,245,.7); font-size:22px; }.ready-countdown { margin:14px 0 30px; color:#fff9e8; font-family:Arial,sans-serif; font-size:61px; font-weight:900; letter-spacing:2px; line-height:1; text-shadow:0 4px 14px rgba(0,0,0,.25); }.ready-divider { width:72%; height:1px; margin:0 auto 28px; background:rgba(211,214,250,.43); }.ready-players { display:flex; justify-content:center; align-items:center; gap:12px; color:#eef0ff; font-size:20px; }.ready-avatar-stack { display:flex; padding-left:16px; }.ready-avatar-stack img { width:37px; height:37px; margin-left:-16px; border:2px solid #202159; border-radius:50%; object-fit:cover; background:#fff; }.ready-meta { display:flex; justify-content:space-between; gap:12px; margin:24px 6px 0; color:rgba(229,231,255,.75); font-size:17px; text-align:left; }.round-description { margin:20px 8px; color:rgba(232,233,255,.85); font-size:19px; line-height:1.5; text-align:left; }.primary-button { width:100%; min-height:68px; border:0; border-radius:34px; color:#fff; background:linear-gradient(180deg,#ff4fa0,#cc0a79); box-shadow:0 10px 22px rgba(188,0,105,.35); font-size:25px; font-weight:800; }.ready-button { margin-top:16px; }.reserved-badge { display:inline-flex; align-items:center; justify-content:center; min-width:250px; min-height:58px; margin-top:18px; padding:0 26px; border:1px solid rgba(253,220,128,.45); border-radius:29px; color:#ffe48a; background:rgba(255,221,109,.1); font-size:21px; font-weight:800; }.hint { margin-top:22px; color:rgba(255,255,255,.82); font-size:21px; text-align:center; }
.live-quiz-page.is-game-playing { min-height:100vh; padding:14px 24px 0; overflow-x:hidden; overflow-y:auto; background:#090126 url('../../static/images/livequiz/ready/bg.jpg') center top/cover no-repeat; }
.is-game-playing:before,.is-game-playing:after { display:none; }
.is-game-playing .live-quiz-content { width:100%; max-width:670px; margin:0 auto; }
.live-experience-chrome { position:fixed; z-index:1200; inset:0; display:flex; width:calc(100% - 56px); max-width:650px; height:100vh; margin:0 auto; padding:46px 0 0; box-sizing:border-box; flex-direction:column; pointer-events:none; }
.live-experience-back { display:flex; width:72px; height:72px; flex:none; align-items:center; justify-content:center; padding:0; border:0; background:transparent; pointer-events:auto; }
.live-experience-back img { display:block; width:100%; height:100%; object-fit:contain; }
.live-experience-prize { width:calc(100% + 2px); aspect-ratio:1045 / 215; flex:none; margin-top:auto; margin-bottom:0; padding:3.6% 5.5% 2.5%; background-size:contain; pointer-events:none; }
.live-experience-prize p { margin-bottom:9px; font-size:clamp(12px,3.35vw,25px); line-height:1.1; white-space:nowrap; }
.live-experience-prize p strong { font-size:1.34em; }
.live-experience-prize p em { font-size:1em; }
.live-experience-prize > div { width:92%; min-height:0; aspect-ratio:744 / 61; margin:0 auto; padding:1.5% 2%; background-size:100% 100%; font-size:clamp(12px,3vw,20px); white-space:nowrap; }
.quiz-game-stage { position:relative; z-index:1; display:flex; width:100%; min-height:calc(100vh - 14px); align-items:center; box-sizing:border-box; flex-direction:column; padding:260px 0 125px; }
.quiz-game-title { position:absolute; z-index:2; top:20px; left:50%; display:block; width:92%; max-width:630px; height:auto; max-height:17vh; object-fit:contain; transform:translateX(-50%); pointer-events:none; }
.quiz-game-stage .reconnect-box { position:relative; z-index:6; width:100%; margin:-10px 0 18px; padding:14px; box-sizing:border-box; border-radius:16px; background:rgba(15,10,60,.94); text-align:center; }
.game-connecting-stage { justify-content:flex-start; }
.game-connecting-message { margin-top:18vh; color:rgba(255,255,255,.82); font-size:clamp(18px,4.5vw,28px); font-weight:800; }
.reconnect-box p { margin:0 0 12px; color:#fff0af; font-size:20px; }.reconnect-button { min-height:52px; padding:0 28px; border:2px solid #fff; border-radius:28px; color:#fff; background:transparent; font-size:22px; font-weight:700; }
.game-question-card { position:relative; display:flex; width:100%; aspect-ratio:959 / 1389; flex:none; flex-direction:column; box-sizing:border-box; margin-top:0; padding:21% 7.2% 11.5%; background:transparent url('../../static/images/livequiz/start/quiz_bg.png') center/contain no-repeat; color:#17172a; }
.question-state-slot { position:absolute; z-index:4; top:-34px; left:50%; display:flex; width:88%; height:156px; align-items:center; justify-content:center; transform:translateX(-50%); pointer-events:none; }
.question-timer { --timer-progress:0deg; position:absolute; z-index:1; top:50%; left:50%; display:flex; width:132px; height:132px; align-items:center; justify-content:center; flex:none; border-radius:50%; background:conic-gradient(#f3164c 0deg,#f3164c var(--timer-progress),#4378da var(--timer-progress),#4378da 360deg); box-shadow:0 4px 12px rgba(13,24,88,.28); transform:translate(-50%,-50%); }
.question-timer:after { content:''; position:absolute; inset:7px; border-radius:50%; background:#f2f1f4; }
.question-timer strong { position:relative; z-index:1; color:#16151f; font-family:Arial,sans-serif; font-size:62px; font-weight:900; line-height:1; }
.question-result-icon { position:absolute; z-index:2; top:50%; left:50%; display:block; width:148px; height:148px; object-fit:contain; transform:translate(-50%,-50%); }
.question-result-title { position:relative; z-index:3; display:block; width:100%; height:132px; object-fit:contain; filter:drop-shadow(0 8px 8px rgba(0,0,0,.28)); }
.answer-reveal-title { position:absolute; width:155%; height:280px; }
.reveal-title-pop-enter-active { animation:reveal-title-pop .56s cubic-bezier(.2,.9,.28,1.25); }
.reveal-title-pop-leave-active { animation:reveal-title-out .28s ease both; }
@keyframes reveal-title-pop { 0% { opacity:0; transform:translateY(16px) scale(.68) rotate(-3deg); } 68% { opacity:1; transform:translateY(-4px) scale(1.08) rotate(1deg); } 100% { opacity:1; transform:translateY(0) scale(1) rotate(0); } }
@keyframes reveal-title-out { 0% { opacity:1; transform:scale(1); } 100% { opacity:0; transform:translateY(-10px) scale(.82); } }
.personal-result-animation { position:absolute; z-index:8; top:-118px; left:50%; display:block; width:140%; height:300px; object-fit:contain; transform:translateX(-50%); filter:drop-shadow(0 12px 10px rgba(0,0,0,.32)); pointer-events:none; }
.personal-result-pop-enter-active { animation:personal-result-pop .58s cubic-bezier(.2,.9,.28,1.25); }
.personal-result-pop-leave-active { transition:opacity .18s ease; }
.personal-result-pop-leave-to { opacity:0; }
@keyframes personal-result-pop { 0% { opacity:0; transform:translateX(-50%) translateY(20px) scale(.68) rotate(-3deg); } 65% { opacity:1; transform:translateX(-50%) translateY(-5px) scale(1.08) rotate(1deg); } 100% { opacity:1; transform:translateX(-50%) translateY(0) scale(1) rotate(0); } }
.quiz-game-stage .question-number { display:flex; width:clamp(150px,40%,205px); aspect-ratio:267 / 56; flex:none; align-self:center; align-items:center; justify-content:center; box-sizing:border-box; margin-bottom:18px; color:#25202c; background:transparent url('../../static/images/livequiz/start/question_bg.png') center/contain no-repeat; font-size:clamp(16px,4.2vw,27px); line-height:1; text-align:center; }
.quiz-game-stage .question-image { display:block; max-width:100%; max-height:70px; margin:0 auto 10px; border-radius:12px; object-fit:contain; }
.quiz-game-stage .question-title { display:flex; min-height:66px; align-items:center; justify-content:center; margin:0 3% 27px; color:#171722; font-size:clamp(18px,4.7vw,31px); font-weight:700; line-height:1.3; text-align:center; }
.answer-list { display:flex; flex-direction:column; gap:14px; }
.quiz-game-stage .answer-option { --answer-fill-width:100%; position:relative; display:flex; width:100%; min-height:0; aspect-ratio:890 / 166; align-items:center; margin:0; padding:8px 7%; overflow:hidden; box-sizing:border-box; border:0; border-radius:999px; color:#171722; background:transparent url('../../static/images/livequiz/start/ans_bg.png') center/contain no-repeat; box-shadow:none; font-size:clamp(18px,4.8vw,31px); font-weight:700; line-height:1.15; text-align:left; }
.quiz-game-stage .answer-option:before { content:''; position:absolute; z-index:0; inset:0; width:100%; background-position:center; background-repeat:no-repeat; background-size:contain; clip-path:inset(0 calc(100% - var(--answer-fill-width)) 0 0 round 999px); transition:clip-path .28s ease; }
.quiz-game-stage .answer-option.selected { border:0; color:#fff; background:transparent url('../../static/images/livequiz/start/selected_bg.png') center/contain no-repeat; }
.quiz-game-stage .answer-option.correct,.quiz-game-stage .answer-option.wrong { color:#171722; background:transparent url('../../static/images/livequiz/start/ans_bg.png') center/contain no-repeat; }
.quiz-game-stage .answer-option.correct:before { background-image:url('../../static/images/livequiz/start/my_correct_ans.png'); }
.quiz-game-stage .answer-option.wrong:before { background-image:url('../../static/images/livequiz/start/my_wrong_ans.png'); }
.answer-text,.answer-result-count,.answer-selected-check { position:relative; z-index:1; }
.answer-text { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.answer-result-count { margin-left:auto; padding-left:12px; flex:none; font-size:.82em; font-weight:500; white-space:nowrap; }
.answer-result-count em { font-style:normal; }
.answer-selected-check { width:46px; height:46px; margin-left:auto; flex:none; object-fit:contain; }
.question-hint { position:absolute; right:8%; bottom:4.5%; left:8%; display:flex; min-height:48px; align-items:center; justify-content:center; margin:0; color:#1d55bd; font-size:clamp(14px,3.8vw,24px); line-height:1.25; text-align:center; }
.question-hint.correct { color:#00a34b; }.question-hint.wrong { color:#ff4778; }.question-hint.spectator { color:#55506a; }
.lobby-state { padding:60px 0; text-align:center; }.lobby-state h2 { font-size:38px; }.lobby-state p { color:#d6d7e7; font-size:23px; }.lobby-state strong { color:#ffdc60; font-size:44px; }
.reward-mask { position:fixed; z-index:1300; inset:0; display:flex; align-items:center; justify-content:center; overflow:hidden; padding:clamp(10px,2vh,24px); box-sizing:border-box; background:rgba(0,0,0,.82); }
.failure-gift-dialog { position:relative; display:flex; width:100%; max-width:620px; max-height:calc(100vh - 20px); align-items:center; box-sizing:border-box; flex-direction:column; padding:clamp(16px,2.5vh,28px) 4px clamp(54px,7vh,74px); border-radius:0; background:transparent; text-align:center; box-shadow:none; animation:failure-gift-dialog-in .36s cubic-bezier(.2,.88,.3,1.12) both; }
.failure-gift-participated { position:relative; z-index:3; margin:0 0 clamp(12px,2vh,22px); color:#fff; font-size:clamp(16px,4.3vw,27px); font-weight:700; line-height:1.2; }
.failure-gift-title { position:relative; z-index:4; display:flex; align-items:center; flex-direction:column; gap:clamp(2px,.5vh,6px); margin-bottom:clamp(-20px,-2vh,-8px); color:#ffcb50; font-style:italic; font-weight:900; line-height:.96; text-transform:uppercase; filter:drop-shadow(0 4px 0 rgba(82,20,73,.45)); }
.failure-gift-title span { font-size:clamp(24px,6.8vw,44px); }
.failure-gift-title strong { color:#ff55b6; font-size:clamp(55px,15vw,96px); letter-spacing:-3px; white-space:nowrap; }
.failure-gift-visual { position:relative; z-index:2; width:112%; max-width:680px; aspect-ratio:1045 / 697; flex:none; margin:0 -6% clamp(2px,.8vh,10px); }
.failure-gift-box { display:block; width:100%; height:100%; object-fit:contain; filter:drop-shadow(0 16px 18px rgba(0,0,0,.3)); animation:failure-gift-box-in .62s .12s cubic-bezier(.18,.9,.2,1.18) both; }
.failure-gift-bubble { position:absolute; z-index:3; top:10%; right:-1%; width:54%; aspect-ratio:508 / 163; }
.failure-gift-bubble img { display:block; width:100%; height:100%; object-fit:contain; }
.failure-gift-bubble strong { position:absolute; top:2%; right:4%; bottom:18%; left:4%; display:flex; align-items:center; justify-content:center; color:#161313; font-size:clamp(15px,4.4vw,28px); font-style:italic; font-weight:900; line-height:1; white-space:nowrap; }
.failure-gift-button { position:relative; display:flex; width:92%; max-width:530px; aspect-ratio:754 / 167; align-items:center; justify-content:center; flex:none; padding:0 5%; overflow:hidden; border:0; color:#fff; background:transparent; font-size:clamp(16px,4.2vw,27px); font-weight:700; }
.failure-gift-button img { position:absolute; z-index:0; inset:0; display:block; width:100%; height:100%; object-fit:fill; }
.failure-gift-button span { position:relative; z-index:1; line-height:1; white-space:nowrap; }
.failure-gift-button:disabled { opacity:.72; }
.failure-gift-claim { margin-top:clamp(4px,.8vh,10px); }
.failure-gift-remind { margin-top:clamp(10px,1.5vh,18px); }
.failure-gift-close { position:absolute; z-index:5; bottom:clamp(-12px,-1.2vh,-6px); left:50%; display:block; width:clamp(52px,13vw,74px); height:clamp(52px,13vw,74px); padding:0; border:0; border-radius:50%; background:transparent; transform:translateX(-50%); }
.failure-gift-close img { display:block; width:100%; height:100%; object-fit:contain; }
@keyframes failure-gift-dialog-in { 0% { opacity:0; transform:translateY(18px) scale(.95); } 100% { opacity:1; transform:translateY(0) scale(1); } }
@keyframes failure-gift-box-in { 0% { opacity:0; transform:translateY(28px) scale(.72) rotate(-4deg); } 64% { opacity:1; transform:translateY(-7px) scale(1.06) rotate(1deg); } 100% { opacity:1; transform:translateY(0) scale(1) rotate(0); } }
@media (max-height:760px) { .failure-gift-dialog { padding-top:12px; padding-bottom:50px; }.failure-gift-participated { margin-bottom:10px; }.failure-gift-title span { font-size:22px; }.failure-gift-title strong { font-size:54px; }.failure-gift-visual { width:90%; margin-bottom:0; }.failure-gift-button { width:82%; }.failure-gift-claim { margin-top:2px; }.failure-gift-remind { margin-top:8px; } }
.final-countdown-mask { position:fixed; z-index:1100; inset:0; display:flex; height:100vh; min-height:0; align-items:center; box-sizing:border-box; flex-direction:column; overflow:hidden; padding:14px 24px 0; background:#090126 url('../../static/images/livequiz/ready/bg.jpg') center top/cover no-repeat; }
.final-countdown-ready { display:block; width:92%; max-width:630px; max-height:21vh; margin:68px auto 0; object-fit:contain; }
.final-countdown-content { position:absolute; top:50%; left:50%; display:flex; width:100%; max-width:670px; align-items:center; justify-content:center; flex-direction:column; transform:translate(-50%,-50%); }
.final-countdown-content p { margin:0 0 2vh; color:rgba(223,207,255,.72); font-size:clamp(22px,5.5vw,38px); font-style:italic; font-weight:800; line-height:1; text-align:center; text-transform:uppercase; }
.final-countdown-number-slot { display:flex; width:min(64vw,430px); height:min(48vh,430px); flex:none; align-items:center; justify-content:center; }
.final-countdown-number { display:block; width:100%; height:100%; object-fit:contain; filter:drop-shadow(0 16px 35px rgba(255,84,180,.22)); }
.is-round-over .live-quiz-content { display:flex; min-height:100vh; flex-direction:column; }
.is-round-over .quiz-game-stage { width:100%; min-height:calc(100vh - 14px); padding:0; }
.round-over-screen { display:flex; width:100%; min-height:calc(100vh - 14px); flex-direction:column; box-sizing:border-box; padding:18px 0 28px; text-align:center; }
.round-over-header { display:flex; align-items:flex-start; justify-content:space-between; gap:14px; }
.round-over-header h1 { display:flex; flex-direction:column; margin:0; color:#fff; font-size:clamp(24px,7vw,52px); font-style:italic; font-weight:900; letter-spacing:-1px; line-height:1.05; text-align:left; text-transform:uppercase; }
.round-over-header h1 strong { margin-top:8px; font:inherit; }
.round-over-actions { display:flex; gap:14px; flex:none; }
.round-over-actions button { display:block; width:clamp(50px,14vw,78px); height:clamp(50px,14vw,78px); padding:0; border:0; border-radius:50%; background:transparent; }
.round-over-actions img { display:block; width:100%; height:100%; object-fit:contain; }
.round-over-settling { display:flex; flex:1; align-items:center; justify-content:flex-start; flex-direction:column; padding-top:19vh; }
.round-over-settling h2,.round-over-result-summary h2 { margin:0; color:#fff; font-size:clamp(48px,13vw,96px); font-style:italic; font-weight:900; letter-spacing:-2px; line-height:.95; text-transform:uppercase; }
.round-over-settling img { display:block; width:86%; max-width:590px; height:auto; margin-top:32px; object-fit:contain; }
.round-over-pass-summary { display:flex; min-height:96px; align-items:center; justify-content:center; flex-direction:column; margin:13vh auto 0; color:#ffd237; font-size:clamp(20px,6vw,44px); line-height:1.1; }
.round-over-pass-row { display:flex; align-items:center; justify-content:center; gap:9px; }
.round-over-pass-row > img { width:clamp(28px,7vw,57px); height:clamp(48px,12vw,96px); object-fit:contain; }
.round-over-pass-row strong { font-weight:500; }
.round-over-pass-summary > p { margin:12px 0 0; font-size:clamp(19px,5vw,36px); line-height:1.1; }
.round-over-avatar-stack { display:flex; padding-left:18px; }
.round-over-avatar-stack img { width:clamp(42px,10vw,72px); height:clamp(42px,10vw,72px); margin-left:-18px; border:2px solid #fff; border-radius:50%; object-fit:cover; background:#fff; }
.round-over-result-summary { margin:7vh auto 0; }
.round-over-result-summary p { margin:21px 0 9px; color:#fff; font-size:clamp(19px,5vw,36px); font-style:italic; line-height:1.2; }
.round-over-result-summary > div,.round-over-next-prize > div { display:flex; align-items:flex-end; justify-content:center; gap:8px; }
.round-over-result-summary strong { color:#ffd14e; font-family:Arial,sans-serif; font-size:clamp(52px,14vw,105px); font-style:italic; font-weight:900; letter-spacing:-4px; line-height:.9; text-shadow:0 6px 16px rgba(255,76,172,.3); }
.round-over-result-summary em,.round-over-next-prize em { padding-bottom:8px; color:#ffe06e; font-size:clamp(20px,5vw,36px); font-style:normal; font-weight:800; }
.round-over-next-card { width:100%; max-width:670px; min-height:430px; margin:5vh auto 0; padding:28px 30px 20px; box-sizing:border-box; background:transparent url('../../static/images/livequiz/match_bg.png') center/100% 100% no-repeat; filter:drop-shadow(0 16px 20px rgba(0,0,0,.36)); }
.round-over-next-heading { display:flex; min-height:70px; align-items:center; justify-content:center; gap:10px; color:#fff; }
.round-over-next-heading img { width:36px; max-height:62px; object-fit:contain; }
.round-over-next-card h3 { margin:0; color:#fff; font-size:clamp(20px,5.7vw,42px); line-height:1.15; }
.round-over-next-prize { min-height:160px; padding:6% 4% 4%; box-sizing:border-box; background:transparent url('../../static/images/livequiz/match_pts_bg.png') center/100% 100% no-repeat; }
.round-over-next-prize p { margin:0 0 2%; color:#fff; font-size:clamp(15px,4.1vw,29px); line-height:1.2; }
.round-over-next-prize strong { display:block; color:#fff36d; background:linear-gradient(180deg,#fffde6 0%,#fff56d 27%,#ffd24a 52%,#ff8a67 76%,#fa42d6 100%) no-repeat; background-size:100% 100%; -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; -webkit-text-stroke:1px rgba(255,247,183,.3); font-family:Arial,sans-serif; font-size:clamp(55px,16vw,120px); font-style:normal; font-weight:900; letter-spacing:-5px; line-height:.9; text-shadow:0 7px 12px rgba(255,54,168,.38); transform:skewX(-12deg); transform-origin:center bottom; }
.round-over-next-card > button { display:block; width:70%; min-height:0; aspect-ratio:754 / 167; margin:25px auto 0; border:0; border-radius:0; color:#fff; background:transparent url('../../static/images/livequiz/btn_bg.png') center/100% 100% no-repeat; font-size:clamp(20px,5.5vw,38px); font-weight:800; }
.round-over-next-card > button:disabled { opacity:1; }
@media (max-height:760px) { .round-over-screen { padding-top:12px; }.round-over-settling { padding-top:15vh; }.round-over-pass-summary { margin-top:8vh; }.round-over-result-summary { margin-top:4vh; }.round-over-next-card { margin-top:3vh; border-radius:34px; } }
.is-lobby-waiting .live-quiz-header { min-height:72px; justify-content:flex-start; }
.is-lobby-waiting .back-button { width:72px; height:72px; }
.is-lobby-waiting .lobby-ready-title { width:92%; max-width:630px; }
.live-quiz-page.is-lobby-waiting { height:100vh; min-height:0; overflow:hidden; padding:46px 28px 0; }
.is-lobby-waiting .live-quiz-header { flex:0 0 72px; }
.is-lobby-waiting .live-quiz-content { min-height:0; }
.is-lobby-waiting .lobby-ready-stage { min-height:0; height:100%; padding:4px 0 120px; }
.is-lobby-waiting .lobby-ready-title { max-height:17vh; margin-bottom:18px; }
.lobby-prize-pool p { display:inline-flex; max-width:100%; align-items:baseline; justify-content:center; gap:5px; text-align:center; white-space:nowrap; }
.live-experience-prize { width:calc(100% + 24px); max-width:710px; margin-left:-12px; padding-left:16px; padding-right:16px; }
.is-lobby-waiting .lobby-timer-card { flex:none; }
</style>



// WEBPACK FOOTER //
// src/page/liveQuiz.vue